import React, { useState, useEffect } from 'react';
import './ActualizarPrecios.css';
import Header from '../common/Header';
import Footer from '../common/Footer';
import { useToast } from '../../contexts/ToastContext';
import { getApiUrl } from '../../utils/api';
import { FaSearch, FaSave, FaFileExcel, FaPrint } from 'react-icons/fa';

const ActualizarPrecios = () => {
  const [productos, setProductos] = useState([]);
  const [productosOriginales, setProductosOriginales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [camposModificados, setCamposModificados] = useState(new Set());
  const toast = useToast();

  // Cargar productos al montar el componente
  useEffect(() => {
    cargarProductos();
    cargarCategorias();
  }, []);

  const cargarProductos = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'getTodosLosPlatillos'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setProductos(data.data);
        setProductosOriginales(JSON.parse(JSON.stringify(data.data))); // Deep copy
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const cargarCategorias = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'getCategorias'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setCategorias(data.data);
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  const actualizarPrecio = (idProducto, nuevoPrecio) => {
    setProductos(prevProductos => 
      prevProductos.map(producto => 
        producto.idProducto === idProducto 
          ? { ...producto, precio: parseFloat(nuevoPrecio) || 0 }
          : producto
      )
    );

    // Verificar si el precio cambió respecto al original
    const original = productosOriginales.find(orig => orig.idProducto === idProducto);
    if (original) {
      const precioOriginal = parseFloat(original.precio) || 0;
      const precioNuevo = parseFloat(nuevoPrecio) || 0;
      
      setCamposModificados(prev => {
        const newSet = new Set(prev);
        if (precioOriginal !== precioNuevo) {
          newSet.add(idProducto);
        } else {
          newSet.delete(idProducto);
        }
        return newSet;
      });
    }
  };

  const guardarCambios = async () => {
    try {
      setGuardando(true);

      // Detectar productos que realmente cambiaron
      const productosModificados = productos.filter(producto => {
        const original = productosOriginales.find(orig => orig.idProducto === producto.idProducto);
        if (!original) return false;
        
        const precioOriginal = parseFloat(original.precio) || 0;
        const precioActual = parseFloat(producto.precio) || 0;
        
        console.log(`Producto ${producto.nombre}: Original=${precioOriginal}, Actual=${precioActual}, Cambio=${precioOriginal !== precioActual}`);
        
        return precioOriginal !== precioActual;
      });

      console.log(`Total productos modificados: ${productosModificados.length}`);

      if (productosModificados.length === 0) {
        toast.info('No se detectaron cambios en los precios.');
        return;
      }

      // Preparar datos para enviar a la API (solo productos modificados)
      const platillosActualizar = productosModificados.map(producto => ({
        idPlatillo: producto.idProducto,
        precio: producto.precio
      }));

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'actualizarPreciosPlatillos',
          platillos: platillosActualizar
        })
      });

      const data = await response.json();
      
      if (data.success) {
        const cantidadModificados = productosModificados.length;
        toast.success(`${cantidadModificados} ${cantidadModificados === 1 ? 'precio actualizado' : 'precios actualizados'} correctamente.`);
        
        // Actualizar los productos originales con los nuevos valores
        setProductosOriginales(JSON.parse(JSON.stringify(productos)));
        // Limpiar campos modificados
        setCamposModificados(new Set());
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al guardar cambios:', error);
      toast.error('Error al guardar los cambios');
    } finally {
      setGuardando(false);
    }
  };

  // Filtrar productos
  const productosFiltrados = productos.filter(producto => {
    const cumpleFiltroNombre = producto.nombre.toLowerCase().includes(filtro.toLowerCase());
    const cumpleFiltroCategoria = !categoriaFiltro || producto.idCategoria.toString() === categoriaFiltro;
    return cumpleFiltroNombre && cumpleFiltroCategoria;
  });

  if (loading) {
    return (
      <div className="actualizar-precios-container">
        <div className="loading">Cargando productos...</div>
      </div>
    );
  }

  return (
    <div className="actualizar-precios-container">
      <Header 
        titulo='Precios' 
        color='#667eea' 
      />
      <div className="actualizar-precios-content">
        <div className="actualizar-precios-inner">
          <div className="precios-header">
            <h1 className="precios-titulo">Actualizar Precios de Platillos</h1>
            <button
              onClick={guardarCambios}
              disabled={guardando}
              className={`btn-guardar-principal ${camposModificados.size > 0 ? 'btn-parpadeando' : ''}`}
            >
              <FaSave /> {guardando ? 'Guardando...' : `Guardar Cambios${camposModificados.size > 0 ? ` (${camposModificados.size})` : ''}`}
            </button>
          </div>

          <div className="panel-filtros">
            <div className="filtros-header">
              <h3 className="filtros-titulo">🔍 Filtros</h3>
            </div>

            <div className="filtros-content">
              <div className="filtros-row-horizontal">
                <div className="filtro-item">
                  <label>Buscar Producto</label>
                  <input
                    type="text"
                    placeholder="Nombre del producto..."
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                    className="input-filtro"
                  />
                </div>
                
                <div className="filtro-item">
                  <label>Filtrar por Categoría</label>
                  <select
                    value={categoriaFiltro}
                    onChange={(e) => setCategoriaFiltro(e.target.value)}
                    className="select-input"
                  >
                    <option value="">📦 Todas las categorías</option>
                    {categorias.map(categoria => (
                      <option key={categoria.idCategoria} value={categoria.idCategoria}>
                        {categoria.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filtro-boton-buscar">
                  <button
                    className="btn-buscar"
                    onClick={() => {}}
                  >
                    <FaSearch />
                    Filtrar
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="panel-listado">
            <div className="tabla-container">
              {loading ? (
                <div className="cargando">Cargando productos...</div>
              ) : productosFiltrados.length === 0 ? (
                <div className="sin-datos">No se encontraron productos que coincidan con los filtros.</div>
              ) : (
                <table className="tabla-precios">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Producto</th>
                      <th>Categoría</th>
                      <th>Precio Actual</th>
                      <th>Nuevo Precio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productosFiltrados.map(producto => (
                      <tr key={producto.idProducto}>
                        <td>{producto.idProducto}</td>
                        <td className="nombre-producto">{producto.nombre}</td>
                        <td className="categoria">{producto.nombreCategoria}</td>
                        <td className="precio-actual">
                          ${producto.precio ? producto.precio.toFixed(2) : '0.00'}
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={producto.precio || ''}
                            onChange={(e) => actualizarPrecio(producto.idProducto, e.target.value)}
                            className={`input-precio ${camposModificados.has(producto.idProducto) ? 'input-modificado' : ''}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="acciones-container">
              <div className="info-productos">
                <span>Total de productos: {productosFiltrados.length}</span>
              </div>

              <div className="exportar-opciones">
                <button className="btn-exportar">
                  <FaFileExcel className="icono-exportar" />
                  Exportar a Excel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ActualizarPrecios;